import {
  useInfiniteQuery,
  useQueryClient,
  InfiniteData,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';
import { useMemo } from 'react';
import { DEFAULT_LIMIT, DEFAULT_RETRY, DEFAULT_STALE_TIME } from '../config';
import type { UseInfinitePaginationOptions, UseInfinitePaginationReturn } from '../types';

/**
 * Unified pagination hook for infinite scrolling.
 * Uses skip/limit pagination pattern.
 * Use this for generic lists (Hero APIs or other); for Hero API lists with entity keys, prefer useInfiniteQueryAPI or domain hooks (e.g. useOrderGetManyAPI).
 */
export function useInfinitePagination<TData = unknown>(
  options: UseInfinitePaginationOptions<TData>,
): UseInfinitePaginationReturn<TData> {
  const {
    queryKey,
    queryFn,
    limit = DEFAULT_LIMIT,
    enabled = true,
    staleTime = DEFAULT_STALE_TIME,
    retry = DEFAULT_RETRY,
  } = options;

  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey,
    enabled,
    queryFn: ({ pageParam = 0 }) => queryFn({ skip: pageParam, limit }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length === 0) {
        return undefined;
      }
      if (lastPage.length < limit) {
        return undefined;
      }
      const totalItemsFetched = allPages.reduce((total, page) => total + (page?.length || 0), 0);
      return totalItemsFetched;
    },
    initialPageParam: 0,
    staleTime,
    retry,
  });

  // Debounce onLoadMore so rapid scroll (or repeated "load more" triggers) don't fire
  // multiple fetchNextPage calls. Without it, we'd request page 2, 3, 4... in quick succession,
  // waste bandwidth, and risk race conditions. 100ms waits for the last trigger then runs once.
  const onLoadMore = useDebouncedCallback(() => {
    if (!query.isFetchingNextPage && query.hasNextPage) {
      query.fetchNextPage();
    }
  }, 100);

  const resetAndRefetch = async () => {
    await queryClient.invalidateQueries({ queryKey });
    await query.refetch();
  };

  // Flatten all pages into one list, then deduplicate by _id. When filters/search change,
  // the query key changes and React Query can refetch; during that transition we can
  // briefly have the same item in multiple pages or old + new data. Dedupe prevents
  // duplicate rows in the UI and avoids React list key warnings.
  const data = useMemo(() => {
    const allData = query.data?.pages.flatMap(page => page || []) || [];
    const seenIds = new Set<string>();
    const uniqueData = allData.filter(item => {
      if (!item || typeof item !== 'object') return true;
      const id = (item as { _id?: { toString?: () => string } | string })._id;
      const idString = id ? (typeof id === 'string' ? id : id.toString?.() || '') : '';
      if (!idString) return true;
      if (seenIds.has(idString)) return false;
      seenIds.add(idString);
      return true;
    });
    return uniqueData;
  }, [query.data]);

  return {
    data,
    onLoadMore,
    hasNextPage: query.hasNextPage ?? false,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    resetAndRefetch,
    query,
  };
}
